import type { WidgetEventPayload } from "@redotech/redo-customer-event-common/widget-event-payloads";
import { BrowserEnvironment, type Environment } from "./environment";
import { createAnonymousIdentity, type AnonymousIdStorage } from "./identity";
import { createImmediateSender } from "./sender";
import { createSessionManager } from "./session";
import type { EventIdentifiers, EventTransport } from "./transport";

export interface RedoMarketingClientOptions {
  transport: EventTransport;
  environment?: Environment;
  anonymousTracking?: boolean;
  anonymousIdStorage?: AnonymousIdStorage;
  sessionTimeoutMs?: number;
}

export interface EventOptions {
  eventId?: string;
}

export interface RedoMarketingClient {
  identify(identifiers?: EventIdentifiers): void;
  track(
    eventName: string,
    args?: Omit<WidgetEventPayload<"custom">, "eventName"> & {
      eventId?: string;
    },
  ): void;
  trackingPageView(
    payload: WidgetEventPayload<"tracking_page_viewed">,
    options?: EventOptions,
  ): void;
  trackingPageClick(
    payload: WidgetEventPayload<"tracking_page_clicked">,
    options?: EventOptions,
  ): void;
  collectionViewed(
    payload: WidgetEventPayload<"collection_viewed">,
    options?: EventOptions,
  ): void;
  productAddedToCart(
    payload: WidgetEventPayload<"product_added_to_cart">,
    options?: EventOptions,
  ): void;
  productViewed(
    payload: WidgetEventPayload<"product_viewed">,
    options?: EventOptions,
  ): void;
  productRemovedFromCart(
    payload: WidgetEventPayload<"product_removed_from_cart">,
    options?: EventOptions,
  ): void;
  pageViewed(
    payload: WidgetEventPayload<"page_viewed">,
    options?: EventOptions,
  ): void;
  checkoutStarted(
    payload: WidgetEventPayload<"checkout_started">,
    options?: EventOptions,
  ): void;
  checkoutContactInfoSubmitted(
    payload: WidgetEventPayload<"checkout_contact_info_submitted">,
    options?: EventOptions,
  ): void;
  checkoutAddressInfoSubmitted(
    payload: WidgetEventPayload<"checkout_address_info_submitted">,
    options?: EventOptions,
  ): void;
  checkoutShippingInfoSubmitted(
    payload: WidgetEventPayload<"checkout_shipping_info_submitted">,
    options?: EventOptions,
  ): void;
  checkoutCompleted(
    payload: WidgetEventPayload<"checkout_completed">,
    options?: EventOptions,
  ): void;
  paymentFailed(
    payload: WidgetEventPayload<"payment_failed">,
    options?: EventOptions,
  ): void;
  flush(): Promise<void>;
}

export function createRedoMarketingClient(
  options: RedoMarketingClientOptions,
): RedoMarketingClient {
  const environment = options.environment ?? new BrowserEnvironment();
  const identity = createAnonymousIdentity(
    environment,
    options.anonymousTracking ?? true,
    options.anonymousIdStorage ?? "localStorage",
  );
  const session = createSessionManager(environment, options.sessionTimeoutMs);
  const sender = createImmediateSender(options.transport);

  const pending = new Set<Promise<void>>();

  const emit = (event: string, payload: unknown, eventId?: string): void => {
    const occurredAt = Date.now();
    const sent = (async () => {
      const customer = await identity.resolve();
      const sessionId = await session.resolve(occurredAt);
      await sender({
        event,
        customer,
        eventId: eventId ?? environment.randomUUID(),
        occurredAt,
        sessionId,
        payload,
      });
    })().finally(() => {
      pending.delete(sent);
    });
    pending.add(sent);
  };

  return {
    identify(identifiers) {
      identity.identify(identifiers);
    },
    track(eventName, args) {
      const { eventId, ...rest } = args ?? {};
      emit("custom", { eventName, ...rest }, eventId);
    },
    trackingPageView(payload, options) {
      emit("tracking_page_viewed", payload, options?.eventId);
    },
    trackingPageClick(payload, options) {
      emit("tracking_page_clicked", payload, options?.eventId);
    },
    collectionViewed(payload, options) {
      emit("collection_viewed", payload, options?.eventId);
    },
    productAddedToCart(payload, options) {
      emit("product_added_to_cart", payload, options?.eventId);
    },
    productViewed(payload, options) {
      emit("product_viewed", payload, options?.eventId);
    },
    productRemovedFromCart(payload, options) {
      emit("product_removed_from_cart", payload, options?.eventId);
    },
    pageViewed(payload, options) {
      emit("page_viewed", payload, options?.eventId);
    },
    checkoutStarted(payload, options) {
      emit("checkout_started", payload, options?.eventId);
    },
    checkoutContactInfoSubmitted(payload, options) {
      emit("checkout_contact_info_submitted", payload, options?.eventId);
    },
    checkoutAddressInfoSubmitted(payload, options) {
      emit("checkout_address_info_submitted", payload, options?.eventId);
    },
    checkoutShippingInfoSubmitted(payload, options) {
      emit("checkout_shipping_info_submitted", payload, options?.eventId);
    },
    checkoutCompleted(payload, options) {
      emit("checkout_completed", payload, options?.eventId);
    },
    paymentFailed(payload, options) {
      emit("payment_failed", payload, options?.eventId);
    },
    async flush() {
      while (pending.size > 0) {
        await Promise.all(pending);
      }
    },
  };
}
